import axios from 'axios';

import { ApiError } from '../common/errors';
import {
    DeleteProgramResponse,
    ProgramCatalogEntry,
    ProgramCatalogHydrationResponse,
    ProgramCatalogHydrationResult,
    ProgramCatalogItem,
    ProgramCatalogItemResponse,
    ProgramCatalogListResponse,
    ProgramCatalogMutationResponse,
    ProgramImageUploadItem,
    ProgramImageUploadResponse,
    ProgramItemResponse,
    ProgramMutationResponse,
    ProgramResponse,
    ProgramWeeks,
    ProgramListResponse,
} from './programs.interface';

const PROGRAMS_ENDPOINT = '/api/programs';
const PROGRAM_CATALOG_ENDPOINT = '/api/programs/catalog';
const PROGRAM_IMAGE_UPLOAD_ENDPOINT = '/api/programs/image-upload';

const createApiError = (error: any, fallbackMessage: string): Error => {
    if (error.response?.status === 404 || error.response?.status === 400) {
        throw new ApiError(error.response?.data?.message || fallbackMessage);
    }

    throw new Error(error.response?.data?.message || error.message || fallbackMessage);
};

export const getPrograms = async (): Promise<string[]> => {
    try {
        const { data } = await axios.get(PROGRAMS_ENDPOINT);

        return (data as ProgramListResponse).items;
    } catch (error: any) {
        return createApiError(error, 'Unable to load programs.');
    }
};

export const getProgram = async (name: string): Promise<ProgramResponse> => {
    try {
        const { data } = await axios.get(`${PROGRAMS_ENDPOINT}/${encodeURIComponent(name)}`);

        return (data as ProgramItemResponse).item;
    } catch (error: any) {
        return createApiError(error, 'Unable to load the program.');
    }
};

export const getProgramCatalog = async (): Promise<ProgramCatalogEntry[]> => {
    try {
        const { data } = await axios.get(PROGRAM_CATALOG_ENDPOINT);

        return (data as ProgramCatalogListResponse).items;
    } catch (error: any) {
        return createApiError(error, 'Unable to load the program catalog.');
    }
};

export const getProgramCatalogItem = async (key: string): Promise<ProgramCatalogEntry> => {
    try {
        const { data } = await axios.get(`${PROGRAM_CATALOG_ENDPOINT}/${encodeURIComponent(key)}`);

        return (data as ProgramCatalogItemResponse).item;
    } catch (error: any) {
        return createApiError(error, 'Unable to load the program metadata.');
    }
};

export const upsertProgramCatalogItem = async (key: string, payload: ProgramCatalogItem): Promise<ProgramCatalogEntry> => {
    try {
        const { data } = await axios.put(`${PROGRAM_CATALOG_ENDPOINT}/${encodeURIComponent(key)}`, payload);

        return (data as ProgramCatalogMutationResponse).item;
    } catch (error: any) {
        return createApiError(error, 'Unable to save the program metadata.');
    }
};

export const hydrateProgramCatalog = async (overwriteExisting = false): Promise<ProgramCatalogHydrationResult> => {
    try {
        const { data } = await axios.post(`${PROGRAM_CATALOG_ENDPOINT}/hydrate`, { overwriteExisting });

        return (data as ProgramCatalogHydrationResponse).item;
    } catch (error: any) {
        return createApiError(error, 'Unable to hydrate the program catalog.');
    }
};

export const createOrReplaceProgram = async (name: string, weeks: ProgramWeeks): Promise<ProgramResponse> => {
    try {
        const { data } = await axios.put(`${PROGRAMS_ENDPOINT}/${encodeURIComponent(name)}`, { weeks });

        return (data as ProgramMutationResponse).item;
    } catch (error: any) {
        return createApiError(error, 'Unable to save the program.');
    }
};

export const deleteProgram = async (name: string): Promise<void> => {
    try {
        await axios.delete<DeleteProgramResponse>(`${PROGRAMS_ENDPOINT}/${encodeURIComponent(name)}`);
    } catch (error: any) {
        return createApiError(error, 'Unable to delete the program.');
    }
};

export const uploadProgramImage = async (
    file: File,
    overrideExistingFile = false,
): Promise<ProgramImageUploadItem> => {
    try {
        const payload = new FormData();

        payload.append('file', file);
        payload.append('overrideExistingFile', overrideExistingFile ? 'true' : 'false');

        const { data } = await axios.post(PROGRAM_IMAGE_UPLOAD_ENDPOINT, payload, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });

        return (data as ProgramImageUploadResponse).item;
    } catch (error: any) {
        if (error.response?.status === 409) {
            const conflictError = new ApiError(error.response?.data?.message || 'An image with this file name already exists.');

            (conflictError as ApiError & { conflict: boolean }).conflict = true;
            throw conflictError;
        }

        if (error.response?.status === 400) {
            throw new ApiError(error.response?.data?.message || 'Unable to upload the program image.');
        }

        throw new Error(error.response?.data?.message || error.message || 'Unable to upload the program image.');
    }
};